import { Component } from '@theme/component';

/** Header measurements and native disclosure navigation. */
class HalloweenHeader extends Component {
  #abort;
  #resize;
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#resize = new ResizeObserver(() => {
      const height = `${this.getBoundingClientRect().height}px`;
      document.body.style.setProperty('--hs-header-height', height);
    });
    this.#resize.observe(this);
    document.addEventListener('click', (event) => {
      for (const details of this.querySelectorAll('details[open]')) {
        if (!details.contains(event.target)) details.open = false;
      }
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('details[open]');
      if (details) { details.open = false; details.querySelector('summary')?.focus(); event.stopPropagation(); }
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) for (const details of this.querySelectorAll('details[open]')) details.open = false;
    }, options);
  }
  disconnectedCallback() { this.#abort?.abort(); this.#resize?.disconnect(); super.disconnectedCallback(); }
}

/** @extends Component<{track: HTMLElement, previous: HTMLButtonElement, next: HTMLButtonElement}> */
class HalloweenProductRail extends Component {
  requiredRefs = ['track'];
  #abort;
  #resize;
  #pointer;
  #dragged = false;
  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  #setup() {
    this.#abort?.abort(); this.#resize?.disconnect();
    this.#abort = new AbortController();
    const track = this.refs.track;
    const options = { signal: this.#abort.signal };
    track.addEventListener('scroll', this.#update, { ...options, passive: true });
    track.addEventListener('pointerdown', (event) => {
      this.#dragged = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,form')) return;
      this.#pointer = { x: event.clientX, scroll: track.scrollLeft, id: event.pointerId };
    }, options);
    window.addEventListener('pointermove', (event) => {
      if (!this.#pointer || event.pointerId !== this.#pointer.id) return;
      const distance = event.clientX - this.#pointer.x;
      if (Math.abs(distance) > 6) this.#dragged = true;
      if (!this.#dragged) return;
      event.preventDefault(); this.dataset.dragging = 'true';
      track.scrollLeft = this.#pointer.scroll - distance;
    }, { ...options, passive: false });
    const end = () => { this.#pointer = undefined; delete this.dataset.dragging; };
    window.addEventListener('pointerup', end, options);
    window.addEventListener('pointercancel', end, options);
    track.addEventListener('dragstart', (event) => event.preventDefault(), options);
    track.addEventListener('click', (event) => {
      if (this.#dragged) { event.preventDefault(); event.stopPropagation(); this.#dragged = false; }
    }, { ...options, capture: true });
    this.#resize = new ResizeObserver(this.#update); this.#resize.observe(track); this.#update();
  }
  #update = () => {
    const track = this.refs.track;
    if (!track) return;
    const position = Math.abs(track.scrollLeft);
    if (this.refs.previous) this.refs.previous.disabled = position < 2;
    if (this.refs.next) this.refs.next.disabled = position >= track.scrollWidth - track.clientWidth - 2;
  };
  previous() { this.#move(-1); }
  next() { this.#move(1); }
  #move(direction) {
    const track = this.refs.track;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * track.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  disconnectedCallback() { this.#abort?.abort(); this.#resize?.disconnect(); super.disconnectedCallback(); }
}

/** @extends Component<{button: HTMLButtonElement}> */
class HalloweenReviewMotion extends Component {
  #observer;
  connectedCallback() {
    super.connectedCallback();
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(([entry]) => { this.dataset.visible = String(entry.isIntersecting); });
    this.#observer.observe(this.closest('.hs-reviews'));
  }
  toggle() {
    this.dataset.paused = String(this.dataset.paused !== 'true');
    this.refs.button.setAttribute('aria-pressed', this.dataset.paused);
    this.refs.button.textContent = this.dataset.paused === 'true' ? this.dataset.play : this.dataset.pause;
  }
  disconnectedCallback() { this.#observer?.disconnect(); super.disconnectedCallback(); }
}

for (const [name, constructor] of [['halloween-header', HalloweenHeader], ['halloween-product-rail', HalloweenProductRail], ['halloween-review-motion', HalloweenReviewMotion]]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
